import React, { useCallback, useEffect, useRef } from "react";
import Usernav from "../partials/usernav";
import Nav from "../partials/nav";
import { MobileNav } from "../partials/mobile-nav";
import { useMediaQuery } from "../../hook/usemediaquery";
import { useSupportedBookies, useBookieCodeData } from "../../api/queries";
import { createOddSchema } from "../../validation/schema";
import { useFormik, ErrorMessage } from "formik";
import { useCreateOdds } from "../../api/mutations";
import { Button, useToast } from "../../lib";
import moment from "moment";
import { useNavigate } from "react-router-dom";

const Post = () => {
  const matches = useMediaQuery("(min-width: 768px)");
  const { data, isLoading, isError } = useSupportedBookies("ng");
  const { mutate, isPending } = useCreateOdds();
  const toast = useToast();
  const ref = useRef();
  const lastFetchedRef = useRef({ bookingCode: "", originBookie: "" });
  const navigate = useNavigate();

  const {
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    touched,
    values,
    setValues,
    setFieldValue,
  } = useFormik({
    initialValues: {
      originBookie: "",
      bookingCode: "",
      bookingCodeType: "FREE",
      totalOdds: 0,
      firstMatchDate: "",
      firstMatchTime: "",
      price: "",
      potentialWinnings: "",
      potentialStake: "",
      bookingCodeScreenshots: [],
    },
    validationSchema: createOddSchema,
    onSubmit: (values) => {
      const obj = {
        totalOdds: Number(values.totalOdds),
      };
      for (let element in values) {
        if (values[element]) {
          obj[element] = values[element];
        }
      }
      delete obj.bookingCodeScreenshots;
      const form = new FormData();
      for (let entry in obj) {
        form.append(entry, obj[entry]);
      }
      Array.from(values.bookingCodeScreenshots ?? []).map((entry) => {
        form.append("bookingCodeScreenshots", entry);
      });
      mutate(form, {
        onSuccess: (data) => {
          toast({
            type: "success",
            message: "Code Published!",
          });
          navigate("/mybets");
        },
        onError: (error) => {
          toast({
            type: "error",
            message: error?.response?.data?.message || "An error occured",
          });
        },
      });
    },
  });
  const {
    refetch,
    data: bookie_code_data,
    isLoading: loading,
  } = useBookieCodeData(
    {
      bookingCode: values.bookingCode,
      originBookie: values.originBookie,
    },
    false
  );

  const handleGetBookingData = useCallback(() => {
    if (values.bookingCode && values.originBookie) {
      if (
        lastFetchedRef.current.bookingCode === values.bookingCode &&
        lastFetchedRef.current.originBookie === values.originBookie
      ) {
        return Promise.resolve();
      }
      lastFetchedRef.current = {
        bookingCode: values.bookingCode,
        originBookie: values.originBookie,
      };

      return refetch()
        .then(({ data }) => {
          const date = data?.firstMatchDate?.split("/");
          setFieldValue("firstMatchDate", date ? `${date[2]}-${date[1]}-${date[0]}` : "");
          setFieldValue("firstMatchTime", data?.firstMatchTime || "");
          setFieldValue("totalOdds", data?.numberOfOdds || "");
        })
        .catch((e) => {
          setFieldValue("firstMatchDate", "");
          setFieldValue("firstMatchTime", "");
          setFieldValue("totalOdds", "");
        });
    }
    return Promise.resolve();
  }, [values.bookingCode, values.originBookie, refetch, setFieldValue]);

  useEffect(() => {
    let timeoutId;
    if (values.bookingCode && values.originBookie) {
      timeoutId = setTimeout(() => {
        handleGetBookingData();
      }, 700);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [values.bookingCode, values.originBookie, handleGetBookingData]);

  return (
    <div>
      {matches ? <Nav></Nav> : <MobileNav />}
      <div className="container lg:px-[160px]">
        <div className="row">
          <div className="col-xxl">
            <h3 className="header-signup mt-8 lg:text-[30px] text-[22px] whitespace-nowrap md:text-[40px]">
              Post a Booking code
            </h3>
            <p className="post-sentence">
              Post booking codes in simple easy steps 😁
            </p>
          </div>
        </div>

        <div className="row padding-form">
          <div className="col-md-6 col-12">
            <select
              name="originBookie"
              value={values.originBookie}
              onBlur={(e) => {
                handleBlur(e);
                handleGetBookingData();
              }}
              onChange={(event) => {
                handleChange(event);
                setFieldValue("totalOdds", "");
                setFieldValue("firstMatchDate", "");
                setFieldValue("firstMatchTime", "");
              }}
              className="input-z"
            >
              <option value="" disabled>
                Select an option
              </option>
              {isLoading && (
                <option value="" disabled>
                  Loading...
                </option>
              )}
              {Array.isArray(data) &&
                data.map((entry) => (
                  <option key={entry.brand} value={entry?.bookie}>
                    {entry?.brand}
                  </option>
                ))}
            </select>
            {!!errors.originBookie && touched.originBookie && (
              <p className="form-field-error">{errors.originBookie}</p>
            )}
            {isError && (
              <p className="form-field-error">
                Failed to load supported bookies. Please refresh or try again
                later.
              </p>
            )}
          </div>

          <div className="col-md-6 col-12">
            <input
              className="input-z"
              placeholder="Booking Code"
              type="text"
              onChange={handleChange}
              onBlur={(e) => {
                handleBlur(e);
                handleGetBookingData();
              }}
              name="bookingCode"
            />
            {!!errors.bookingCode && touched.bookingCode && (
              <p className="form-field-error">{errors.bookingCode}</p>
            )}
          </div>

          <div className="col-md-6 col-12">
            <input
              className="input-z"
              placeholder="Total Odds"
              type="text"
              onChange={handleChange}
              value={values.totalOdds}
              name="totalOdds"
            />
            {!!errors.totalOdds && touched.totalOdds && (
              <p className="form-field-error">{errors.totalOdds}</p>
            )}
          </div>

          <div className="col-md-6 col-12">
            <input
              className="input-z"
              placeholder="First Match Date"
              type="text"
              onChange={handleChange}
              value={values.firstMatchDate}
            />
          </div>
          <div className="col-md-6 col-12">
            <input
              className="input-z"
              placeholder="First Match Time (FMT)"
              type="text"
              onChange={handleChange}
              value={values.firstMatchTime}
            />
          </div>
          <div className="col-md-6 col-12">
            <input
              className="input-z"
              placeholder="Potential winning"
              type="text"
              onChange={handleChange}
              onBlur={handleBlur}
              name="potentialWinnings"
              value={values.potentialWinnings}
            />
          </div>
          <div className="col-md-6 col-12">
            <input
              className="input-z"
              placeholder="Potential Stake"
              type="text"
              onChange={handleChange}
              onBlur={handleBlur}
              name="potentialStake"
              value={values.potentialStake}
            />
          </div>
          <div className="col-md-6 col-12">
            <div
              className="input-z h-[60px] cursor-pointer flex justify-start items-center pl-4"
              onClick={() => ref.current?.click()}
            >
              <p className="input-z-p opacity-30">Pick file</p>
            </div>
            {!!errors.bookingCodeScreenshots &&
              touched.bookingCodeScreenshots && (
                <p className="form-field-error">
                  {errors.bookingCodeScreenshots}
                </p>
              )}
            <div>
              {Array.from(values.bookingCodeScreenshots ?? []).map(
                (element) => (
                  <p className="input-z-p" key={element.name}>
                    {element.name}
                  </p>
                )
              )}
            </div>

            <input
              className="input-z hidden"
              placeholder="Potential Stake"
              type="file"
              ref={ref}
              multiple
              onChange={(event) =>
                setFieldValue("bookingCodeScreenshots", event.target.files)
              }
            />
          </div>
          <div className="col-md-6 col-12">
            <Button onClick={handleSubmit} loading={isPending}>
              Post Booking code
            </Button>
          </div>

          <div className="col-md-6 col-12">
            <p className="pre-text">
              First Match Date (FMD) & First Match Time (FMT) are important and
              must be accurate as it helps to keep your Odds valid to be copied
              by other users
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Post;
